<template>
  <div v-click-outside="onClickOutside">
    <div class="w-full">
      <div
        class="relative cursor-pointer rounded-md shadow ring-1"
        :class="[dateIsInvalid ? 'ring-red-500' : 'ring-gray-400']"
      >
        <div @click="showDatepicker = !showDatepicker">
          <input v-model="date" type="hidden" name="date" />
          <input
            v-model="humanDatePickerValue"
            type="text"
            readonly
            class="w-full cursor-pointer rounded-md border-none py-2 pr-10 pl-4 text-sm font-medium text-gray-700"
            :class="buttonClass"
            placeholder="Select date"
          />
          <div class="absolute inset-y-0 right-0 flex items-center pr-2">
            <Icon name="calendar-alt" class="size-5 text-gray-400" />
          </div>
        </div>

        <Transition name="fade-250">
          <div
            v-if="showDatepicker"
            class="absolute top-0 left-0 z-40 mt-10 rounded-lg bg-white p-4 shadow-lg ring-1 ring-gray-400"
          >
            <div class="mb-2 flex items-center justify-between">
              <div>
                <span class="text-lg font-bold">
                  {{ MONTH_NAMES[month] }}
                </span>
                <span class="ml-1 text-lg font-normal text-gray-600">
                  {{ year }}
                </span>
              </div>
              <div class="flex space-x-2">
                <button
                  type="button"
                  class="inline-flex cursor-pointer rounded-full p-1 transition duration-100 ease-in-out hover:bg-gray-200 focus:ring-2 focus:outline-none"
                  @click="decrementMonth()"
                >
                  <Icon
                    name="angle-right"
                    class="inline-flex size-6 rotate-180 text-gray-500"
                  />
                </button>
                <button
                  type="button"
                  class="inline-flex cursor-pointer rounded-full p-1 transition duration-100 ease-in-out hover:bg-gray-200 focus:ring-2 focus:outline-none"
                  @click="goToThisMonth()"
                >
                  <svg
                    class="inline-flex size-6 text-gray-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="{2}"
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  class="inline-flex cursor-pointer rounded-full p-1 transition duration-100 ease-in-out hover:bg-gray-200 focus:ring-2 focus:outline-none"
                  @click="incrementMonth()"
                >
                  <Icon
                    name="angle-right"
                    class="inline-flex size-6 text-gray-500"
                  />
                </button>
              </div>
            </div>

            <div class="-mx-1 mb-3 flex flex-wrap">
              <div
                v-for="dayName in DAY_NAMES"
                :key="dayName"
                class="w-1/7 px-1"
              >
                <div class="text-center text-xs font-medium">
                  {{ dayName }}
                </div>
              </div>
            </div>

            <div class="-mx-1 flex flex-wrap">
              <div
                v-for="(blankday, index) in blankDays"
                :key="index"
                class="w-1/7 border border-transparent p-1 text-center text-sm"
              />
              <div
                v-for="(availableDay, index) in availableDays"
                :key="index"
                class="mb-1 w-1/7 text-center"
              >
                <button
                  class="cursor-pointer rounded-full py-1 text-sm leading-none transition duration-100 ease-in-out focus:ring focus:outline-none"
                  type="button"
                  :class="[
                    {
                      'bg-orange-500 text-white': isSelectedDate(availableDay),
                      'bg-blue-500 text-white':
                        isToday(year, month, availableDay) &&
                        !isSelectedDate(availableDay),
                      'text-gray-700 hover:bg-blue-200':
                        !isToday(year, month, availableDay) &&
                        !isSelectedDate(availableDay),
                    },
                    availableDay < 10 ? 'px-2' : 'px-1',
                  ]"
                  @click="setDateValue(availableDay)"
                >
                  {{ availableDay }}
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
    <p v-if="dateIsInvalid" class="mt-1 text-right text-sm text-red-500">
      {{
        dateInvalidationReason !== "" ? dateInvalidationReason : "Invalid Date"
      }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, PropType, watch } from "vue";
import { vClickOutside } from "@/utils/directives";
import * as DateUtils from "@/utils/dates";
import { DateTime, Info } from "luxon";
import { isToday } from "@/utils/dates";
import Icon from "@/components/Core/Icon.vue";

/**
 * Inspired by https://tailwindcomponents.com/component/datepicker-with-tailwindcss-and-alpinejs
 */
// TODO: Add min-date/max-date support
// TODO: highlight start-to-end dates
const props = defineProps({
  modelValue: {
    type: Object as PropType<DateTime>,
    default: () => DateTime.local(),
  },
  dateIsInvalid: {
    type: Boolean,
    default: false,
  },
  dateInvalidationReason: {
    type: String,
    default: "",
  },
  buttonClass: {
    type: String,
    default: "",
  },
});
const emit = defineEmits({ "update:modelValue": (_date: DateTime) => true });
const DAY_NAMES = Info.weekdays("short");
const MONTH_NAMES = Info.months("short");

// TODO: Refactor the component to use DateTime internally
const datePickerValue = computed({
  get() {
    return props.modelValue.toJSDate();
  },
  set(value: Date) {
    emit("update:modelValue", DateTime.fromJSDate(value));
  },
});
// eslint-disable-next-line vue/no-ref-object-reactivity-loss
const date = ref(datePickerValue.value);
const showDatepicker = ref(false);

watch(datePickerValue, () => {
  date.value = datePickerValue.value;
});

const year = computed(() => {
  return date.value.getFullYear();
});

const month = computed(() => {
  return date.value.getMonth();
});

const day = computed(() => {
  return date.value.getDate();
});

const blankDays = computed(() => {
  const dayOfWeek = new Date(year.value, month.value, 0).getDay();
  const blankDays = [];
  for (let i = 1; i <= dayOfWeek; i++) {
    blankDays.push(i);
  }
  return blankDays;
});

const availableDays = computed(() => {
  const daysInMonth = new Date(year.value, month.value + 1, 0).getDate();

  const days = [];
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }
  return days;
});

const humanDatePickerValue = computed(() => {
  return DateTime.fromJSDate(datePickerValue.value).toLocaleString();
});

function isSelectedDate(date: Date | number): boolean {
  if (typeof date === "number") {
    return DateUtils.isSameDate(
      datePickerValue.value,
      new Date(year.value, month.value, date),
    );
  }
  return DateUtils.isSameDate(datePickerValue.value, date);
}

function decrementMonth(): void {
  const newMonth = new Date(year.value, month.value, day.value);
  newMonth.setMonth(newMonth.getMonth() - 1);
  date.value = newMonth;
}

function incrementMonth(): void {
  const newMonth = new Date(year.value, month.value, day.value);
  newMonth.setMonth(newMonth.getMonth() + 1);
  date.value = newMonth;
}

function goToThisMonth(): void {
  const today = new Date();
  const thisYearAndMonth = new Date();
  thisYearAndMonth.setFullYear(today.getFullYear());
  thisYearAndMonth.setMonth(today.getMonth());
  date.value = thisYearAndMonth;
}

function setDateValue(date: Date | number): void {
  let selectedDate: Date;

  if (date instanceof Date) {
    selectedDate = new Date(year.value, month.value, date.getDate());
  } else {
    selectedDate = new Date(year.value, month.value, date);
  }

  showDatepicker.value = false;
  datePickerValue.value = selectedDate;
}

function onClickOutside(): void {
  if (showDatepicker.value === true) {
    showDatepicker.value = false;
  }
}
</script>
